import React, { useState, useEffect } from 'react';
import { Ruler } from 'lucide-react';
import { SEO } from '../components/SEO';

interface JeansSizeState {
  waist: string;
  hip: string;
  inseam: string;
  gender: 'male' | 'female';
  region: 'eu' | 'us' | 'uk';
  fit: 'slim' | 'regular' | 'relaxed';
}

interface JeansSizeResult {
  waistSize: string;
  lengthSize: string | null;
  fullSize: string;
  alternativeSizes: {
    eu: string;
    us: string;
    uk: string;
  };
  recommendedFit: string;
}

function KalkulatorVelicineTraperica() {
  const [values, setValues] = useState<JeansSizeState>({
    waist: '',
    hip: '',
    inseam: '',
    gender: 'male',
    region: 'eu',
    fit: 'regular'
  });

  const [results, setResults] = useState<JeansSizeResult | null>(null);

  // Size conversion tables
  const maleSizes = {
    waist: {
      eu: [
        { min: 68, max: 72, size: '44' },
        { min: 72, max: 76, size: '46' },
        { min: 76, max: 80, size: '48' },
        { min: 80, max: 84, size: '50' },
        { min: 84, max: 88, size: '52' },
        { min: 88, max: 92, size: '54' },
        { min: 92, max: 96, size: '56' },
        { min: 96, max: 100, size: '58' },
        { min: 100, max: 104, size: '60' }
      ],
      us: [
        { min: 68, max: 72, size: '28' },
        { min: 72, max: 76, size: '30' },
        { min: 76, max: 80, size: '32' },
        { min: 80, max: 84, size: '34' },
        { min: 84, max: 88, size: '36' },
        { min: 88, max: 92, size: '38' },
        { min: 92, max: 96, size: '40' },
        { min: 96, max: 100, size: '42' },
        { min: 100, max: 104, size: '44' }
      ],
      uk: [
        { min: 68, max: 72, size: '28' },
        { min: 72, max: 76, size: '30' },
        { min: 76, max: 80, size: '32' },
        { min: 80, max: 84, size: '34' },
        { min: 84, max: 88, size: '36' },
        { min: 88, max: 92, size: '38' },
        { min: 92, max: 96, size: '40' },
        { min: 96, max: 100, size: '42' },
        { min: 100, max: 104, size: '44' }
      ]
    },
    length: [
      { min: 74, max: 78, size: '30' },
      { min: 78, max: 82, size: '32' },
      { min: 82, max: 86, size: '34' },
      { min: 86, max: 90, size: '36' }
    ]
  };

  const femaleSizes = {
    waist: {
      eu: [
        { min: 58, max: 62, size: '34' },
        { min: 62, max: 66, size: '36' },
        { min: 66, max: 70, size: '38' },
        { min: 70, max: 74, size: '40' },
        { min: 74, max: 78, size: '42' },
        { min: 78, max: 82, size: '44' },
        { min: 82, max: 86, size: '46' },
        { min: 86, max: 90, size: '48' }
      ],
      us: [
        { min: 58, max: 62, size: '00' },
        { min: 62, max: 66, size: '0' },
        { min: 66, max: 70, size: '2' },
        { min: 70, max: 74, size: '4' },
        { min: 74, max: 78, size: '6' },
        { min: 78, max: 82, size: '8' },
        { min: 82, max: 86, size: '10' },
        { min: 86, max: 90, size: '12' }
      ],
      uk: [
        { min: 58, max: 62, size: '4' },
        { min: 62, max: 66, size: '6' },
        { min: 66, max: 70, size: '8' },
        { min: 70, max: 74, size: '10' },
        { min: 74, max: 78, size: '12' },
        { min: 78, max: 82, size: '14' },
        { min: 82, max: 86, size: '16' },
        { min: 86, max: 90, size: '18' }
      ]
    },
    length: [
      { min: 70, max: 74, size: '28' },
      { min: 74, max: 78, size: '30' },
      { min: 78, max: 82, size: '32' },
      { min: 82, max: 86, size: '34' }
    ]
  };

  const calculateJeansSize = () => {
    const waist = parseFloat(values.waist);
    const hip = parseFloat(values.hip);
    const inseam = parseFloat(values.inseam);
    
    if (isNaN(waist)) {
      return null;
    }

    // Determine which size table to use
    const sizeTable = values.gender === 'male' ? maleSizes : femaleSizes;
    
    // Find waist size based on region
    const waistSizeTable = sizeTable.waist[values.region];
    let waistSize = '';
    
    for (const entry of waistSizeTable) {
      if (waist >= entry.min && waist < entry.max) {
        waistSize = entry.size;
        break;
      }
    }
    
    // If no match found, use the closest size
    if (!waistSize) {
      if (waist < waistSizeTable[0].min) {
        waistSize = waistSizeTable[0].size;
      } else {
        waistSize = waistSizeTable[waistSizeTable.length - 1].size;
      }
    }
    
    // Find length size if inseam is provided
    let lengthSize = null;
    if (!isNaN(inseam)) {
      for (const entry of sizeTable.length) {
        if (inseam >= entry.min && inseam < entry.max) {
          lengthSize = entry.size;
          break;
        }
      }
      
      // If no match found, use the closest size
      if (!lengthSize) {
        if (inseam < sizeTable.length[0].min) {
          lengthSize = sizeTable.length[0].size;
        } else {
          lengthSize = sizeTable.length[sizeTable.length.length - 1].size;
        }
      }
    }
    
    // Create full size (W/L format for US/UK, just waist for EU)
    const fullSize = values.region === 'eu' 
      ? waistSize 
      : `W${waistSize}${lengthSize ? `/L${lengthSize}` : ''}`;
    
    // Find alternative sizes in other regions
    const alternativeSizes = {
      eu: '',
      us: '',
      uk: ''
    };
    
    // Find the equivalent size in each region
    for (const region of ['eu', 'us', 'uk'] as const) {
      if (region === values.region) {
        alternativeSizes[region] = fullSize;
        continue;
      }
      
      // Find the equivalent waist size in the other region
      const altWaistTable = sizeTable.waist[region];
      let altWaistSize = '';
      
      for (let i = 0; i < waistSizeTable.length; i++) {
        if (waistSizeTable[i].size === waistSize && i < altWaistTable.length) {
          altWaistSize = altWaistTable[i].size;
          break;
        }
      }
      
      // If no match found, use approximate conversion
      if (!altWaistSize) {
        for (const entry of altWaistTable) {
          if (waist >= entry.min && waist < entry.max) {
            altWaistSize = entry.size;
            break;
          }
        }
      }
      
      alternativeSizes[region] = region === 'eu' 
        ? altWaistSize 
        : `W${altWaistSize}${lengthSize ? `/L${lengthSize}` : ''}`;
    }
    
    // Recommend fit based on waist-to-hip ratio
    let recommendedFit = values.fit;
    if (!isNaN(hip) && hip > 0) {
      const waistHipRatio = waist / hip;
      
      if (values.gender === 'male') {
        if (waistHipRatio < 0.85) recommendedFit = 'slim';
        else if (waistHipRatio > 0.95) recommendedFit = 'relaxed';
        else recommendedFit = 'regular';
      } else {
        if (waistHipRatio < 0.75) recommendedFit = 'slim';
        else if (waistHipRatio > 0.85) recommendedFit = 'relaxed';
        else recommendedFit = 'regular';
      }
    }
    
    return {
      waistSize,
      lengthSize,
      fullSize,
      alternativeSizes,
      recommendedFit
    };
  };

  const handleInputChange = (field: keyof JeansSizeState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.waist) {
      const results = calculateJeansSize();
      setResults(results);
    }
  }, [values]);

  const fitNames = {
    slim: 'Uski kroj (Slim Fit)',
    regular: 'Standardni kroj (Regular Fit)',
    relaxed: 'Opušteni kroj (Relaxed Fit)'
  };

  const getFitDescription = (fit: string) => {
    switch (fit) {
      case 'slim':
        return 'Uski kroj koji prati liniju tijela, uži u području bedara i nogu.';
      case 'regular':
        return 'Standardni kroj koji nije ni preuzak ni preširok, udoban za većinu ljudi.';
      case 'relaxed':
        return 'Opušteniji kroj koji pruža više prostora u području bedara i nogu.';
      default:
        return '';
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Veličine Traperica",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte svoju idealnu veličinu traperica. Precizni izračuni za EU, UK i US veličine s preporukama za najbolji kroj.",
    "url": "https://online-kalkulator.com/kalkulator-velicine-traperica",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun veličine traperica",
      "Podrška za EU, UK i US veličine",
      "Preporuka kroja",
      "Konverzija između različitih standarda",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Veličine Traperica | Izračun Veličine Hlača"
        description="Izračunajte svoju idealnu veličinu traperica. Precizni izračuni za EU, UK i US veličine s preporukama za najbolji kroj."
        canonicalUrl="/kalkulator-velicine-traperica"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Ruler className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Veličine Traperica</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Gender Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Spol:
                </label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="male"
                      checked={values.gender === 'male'}
                      onChange={() => handleInputChange('gender', 'male')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Muški</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="female"
                      checked={values.gender === 'female'}
                      onChange={() => handleInputChange('gender', 'female')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Ženski</span>
                  </label>
                </div>
              </div>

              {/* Region Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Standard veličina:
                </label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="eu"
                      checked={values.region === 'eu'}
                      onChange={() => handleInputChange('region', 'eu')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Europski (EU)</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="us"
                      checked={values.region === 'us'}
                      onChange={() => handleInputChange('region', 'us')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Američki (US)</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="uk"
                      checked={values.region === 'uk'}
                      onChange={() => handleInputChange('region', 'uk')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Britanski (UK)</span>
                  </label>
                </div>
              </div>

              {/* Fit Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Željeni kroj:
                </label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="slim"
                      checked={values.fit === 'slim'}
                      onChange={() => handleInputChange('fit', 'slim')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Uski (Slim)</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="regular"
                      checked={values.fit === 'regular'}
                      onChange={() => handleInputChange('fit', 'regular')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Standardni (Regular)</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="relaxed"
                      checked={values.fit === 'relaxed'}
                      onChange={() => handleInputChange('fit', 'relaxed')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Opušteni (Relaxed)</span>
                  </label>
                </div>
              </div>

              {/* Measurements */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opseg struka (cm)
                  </label>
                  <input
                    type="number"
                    value={values.waist}
                    onChange={(e) => handleInputChange('waist', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`npr. ${values.gender === 'male' ? '82' : '70'}`}
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opseg bokova (cm)
                  </label>
                  <input
                    type="number"
                    value={values.hip}
                    onChange={(e) => handleInputChange('hip', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`npr. ${values.gender === 'male' ? '92' : '95'}`}
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unutarnja duljina noge (cm)
                  </label>
                  <input
                    type="number"
                    value={values.inseam}
                    onChange={(e) => handleInputChange('inseam', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`npr. ${values.gender === 'male' ? '82' : '78'}`}
                    step="0.1"
                  />
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-blue-50">
                    <p className="text-sm text-gray-600 mb-1">Vaša veličina traperica:</p>
                    <p className="text-2xl font-bold text-blue-600">{results.fullSize}</p>
                    {values.region !== 'eu' && (
                      <p className="text-sm text-gray-600 mt-1">
                        Struk: {results.waistSize} | Duljina: {results.lengthSize || 'Standardna'}
                      </p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Veličine u drugim standardima:</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="p-2 bg-gray-50 rounded text-center">
                        <p className="text-xs text-gray-500">EU</p>
                        <p className="font-medium">{results.alternativeSizes.eu}</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded text-center">
                        <p className="text-xs text-gray-500">US</p>
                        <p className="font-medium">{results.alternativeSizes.us}</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded text-center">
                        <p className="text-xs text-gray-500">UK</p>
                        <p className="font-medium">{results.alternativeSizes.uk}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Preporučeni kroj:</h3>
                    <p className="text-blue-600 font-medium">{fitNames[results.recommendedFit as keyof typeof fitNames]}</p>
                    <p className="text-sm text-gray-600 mt-1">{getFitDescription(results.recommendedFit)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Pravilno Izmjeriti</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Opseg struka</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Izmjerite najuži dio struka, obično oko 2-3 cm iznad pupka</li>
                      <li>Držite mjernu vrpcu vodoravno i ne stežite je previše</li>
                      <li>Mjerenje obavite u opuštenom stojećem položaju</li>
                      <li>Ne zadržavajte dah i ne uvlačite trbuh tijekom mjerenja</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Opseg bokova</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Izmjerite najširi dio bokova, obično oko 20 cm ispod struka</li>
                      <li>Mjerna vrpca treba prelaziti preko najistaknutijeg dijela stražnjice</li>
                      <li>Držite mjernu vrpcu vodoravno i paralelno s podom</li>
                      <li>Mjerenje obavite u opuštenom stojećem položaju</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Unutarnja duljina noge (inseam)</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Izmjerite od prepona do gležnja</li>
                      <li>Najbolje je mjeriti na hlačama koje vam dobro pristaju</li>
                      <li>Mjerite duž unutarnjeg šava noge</li>
                      <li>Za preciznije mjerenje, zamolite nekoga da vam pomogne</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Standardi Veličina</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Europski (EU) standard</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Muške veličine: 44, 46, 48, 50, 52, 54, 56, 58, 60</li>
                      <li>Ženske veličine: 34, 36, 38, 40, 42, 44, 46, 48</li>
                      <li>Baziran na opsegu struka u centimetrima</li>
                      <li>Duljina se obično označava kao kratka, standardna ili duga</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Američki (US) standard</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Muške veličine: W28-W44 (struk u inčima)</li>
                      <li>Ženske veličine: 00, 0, 2, 4, 6, 8, 10, 12, 14, 16</li>
                      <li>Duljina se označava kao L30, L32, L34, L36 (duljina u inčima)</li>
                      <li>Format za muškarce: W32/L34 (struk 32 inča, duljina 34 inča)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Britanski (UK) standard</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Muške veličine: slične američkim (W28-W44)</li>
                      <li>Ženske veličine: 4, 6, 8, 10, 12, 14, 16, 18 (za 2 veće od US)</li>
                      <li>Duljina se također označava u inčima (L30, L32, itd.)</li>
                      <li>Format za muškarce: W32/L34 (struk 32 inča, duljina 34 inča)</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Tipovi Kroja Traperica</h2>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Uski kroj (Slim Fit)</h3>
                    <p className="text-gray-600">
                      Uski kroj koji prati liniju tijela. Uži je u području bedara i nogu, stvarajući
                      elegantniji izgled. Idealan za osobe vitke građe ili one koji žele naglasiti
                      liniju tijela.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Standardni kroj (Regular Fit)</h3>
                    <p className="text-gray-600">
                      Klasični kroj koji nije ni preuzak ni preširok. Pruža udobnost i slobodu kretanja,
                      a odgovara većini tipova tijela. Dobar izbor za svakodnevno nošenje.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Opušteni kroj (Relaxed Fit)</h3>
                    <p className="text-gray-600">
                      Prostraniji kroj koji pruža više prostora u području bokova, bedara i nogu.
                      Idealan za osobe robusnije građe ili one koji preferiraju udobnost nad stilom.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Kupovinu Traperica</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Uvijek isprobajte traperice prije kupnje kad god je to moguće</li>
                  <li>Veličine mogu varirati između različitih brendova</li>
                  <li>Traperice se obično rastežu nakon nošenja, pa bi trebale biti malo uže pri kupnji</li>
                  <li>Obratite pažnju na sastav materijala - veći postotak elastina znači više rastezanja</li>
                  <li>Visina struka (niska, srednja, visoka) također utječe na pristajanje</li>
                  <li>Ako ste između dvije veličine, odaberite veću za opušteni kroj ili manju za uži kroj</li>
                  <li>Duljina treba biti takva da traperice lagano dodiruju vrh cipela</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti Kalkulator</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite spol</li>
                  <li>Odaberite standard veličina koji preferirate (EU, US, UK)</li>
                  <li>Odaberite željeni kroj traperica</li>
                  <li>Unesite opseg struka u centimetrima</li>
                  <li>Opcionalno unesite opseg bokova za precizniju preporuku kroja</li>
                  <li>Opcionalno unesite unutarnju duljinu noge za preporuku duljine</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Najčešći Problemi s Veličinom</h2>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium text-gray-700">Traperice su preuske u struku</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Odaberite veću veličinu ili model s višim udjelom elastina.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium text-gray-700">Traperice su preširoke u struku, ali odgovaraju u bokovima</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Potražite modele s oznakama "Curvy" ili "Shaped" koji su dizajnirani za veći omjer bokova i struka.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium text-gray-700">Traperice su prekratke</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Potražite modele s oznakom "Long" ili većim brojem duljine (L34, L36).
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium text-gray-700">Traperice su preduge</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Potražite modele s oznakom "Short" ili manjim brojem duljine (L28, L30).
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Veličine traperica mogu značajno varirati između različitih brendova</li>
                  <li>Ovaj kalkulator daje okvirnu procjenu, a ne apsolutnu veličinu</li>
                  <li>Najbolje je isprobati traperice prije kupnje kad god je to moguće</li>
                  <li>Različiti stilovi traperica (bootcut, skinny, straight) mogu zahtijevati različite veličine</li>
                  <li>Sastav materijala utječe na pristajanje - traperice s elastinom će se više rastegnuti</li>
                  <li>Ako kupujete online, provjerite tablicu veličina specifičnu za taj brend</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Održavanje Traperica</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Perite traperice naopako kako biste sačuvali boju</li>
                  <li>Koristite hladnu vodu i blagi deterdžent</li>
                  <li>Izbjegavajte sušilicu - prirodno sušenje produljuje vijek trajanja</li>
                  <li>Ne perite traperice nakon svakog nošenja - dovoljno je nakon 4-5 nošenja</li>
                  <li>Za osvježavanje između pranja, objesite traperice na svježem zraku</li>
                  <li>Kvalitetne traperice s vremenom će se prilagoditi vašem tijelu</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorVelicineTraperica;